import { Directive, ElementRef, Input, OnChanges, SimpleChanges, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

@Directive({
  selector: '[markdown]'
})
export class MarkdownDirective implements OnChanges {
  @Input() markdown: string;
  @Input() markdownInline: boolean = false;

  constructor(
    private el: ElementRef,
    private sanitizer: DomSanitizer
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['markdown']) {
      this.render();
    }
  }

  private render(): void {
    if (!this.markdown) {
      this.el.nativeElement.innerHTML = '';
      return;
    }

    const html = this.markdownInline ? this.inlineMarkdownToHtml(this.markdown) : this.markdownToHtml(this.markdown);
    // Sanitize HTML to prevent XSS attacks
    const sanitized = this.sanitizer.sanitize(SecurityContext.HTML, html);
    this.el.nativeElement.innerHTML = sanitized || '';
  }

  private inlineMarkdownToHtml(markdown: string): string {
    let html = markdown;

    // Escape HTML special characters first
    html = this.escapeHtml(html);

    // Process inline code (must be before bold/italic to avoid conflicts)
    html = html.replace(/`(.+?)`/g, '<code>$1</code>');

    // Process bold (** or __)
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

    // Process italics (* or _) - must be after bold
    html = html.replace(/\*([^\*]+?)\*/g, '<em>$1</em>');
    html = html.replace(/_([^_]+?)_/g, '<em>$1</em>');

    // Convert line breaks to <br> (no paragraph wrapping in inline mode)
    html = html.replace(/\n/g, '<br>');

    return html;
  }

  private markdownToHtml(markdown: string): string {
    let html = markdown;

    // Escape HTML special characters first (except those we'll use for markdown)
    html = this.escapeHtml(html);

    // Process fenced code blocks (must be before inline code)
    html = this.processFencedCodeBlocks(html);

    // Process blockquotes
    html = this.processBlockquotes(html);

    // Process headings (h1-h6)
    html = this.processHeadings(html);

    // Process bold (** or __)
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

    // Process italics (* or _)
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
    html = html.replace(/_(.+?)_/g, '<em>$1</em>');

    // Process inline code
    html = html.replace(/`(.+?)`/g, '<code>$1</code>');

    // Process unordered lists
    html = this.processUnorderedLists(html);

    // Process ordered lists
    html = this.processOrderedLists(html);

    // Process paragraphs (double line breaks)
    html = this.processParagraphs(html);

    // Convert single line breaks to <br>
    html = html.replace(/\n/g, '<br>');

    return html;
  }

  private escapeHtml(text: string): string {
    const map: { [key: string]: string } = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  }

  private processFencedCodeBlocks(html: string): string {
    // Match ```language\ncode\n``` or ```\ncode\n```
    return html.replace(/```(\w+)?\n([\s\S]*?)```/g, (match, language, code) => {
      const langClass = language ? ` class="language-${this.escapeHtml(language)}"` : '';
      return `<pre><code${langClass}>${code.trim()}</code></pre>`;
    });
  }

  private processBlockquotes(html: string): string {
    const lines = html.split('\n');
    let inBlockquote = false;
    let blockquoteContent: string[] = [];
    const result: string[] = [];

    for (const line of lines) {
      if (line.trim().startsWith('&gt; ')) {
        if (!inBlockquote) {
          inBlockquote = true;
          blockquoteContent = [];
        }
        blockquoteContent.push(line.trim().substring(5)); // Remove '> '
      } else {
        if (inBlockquote) {
          result.push(`<blockquote>${blockquoteContent.join('\n')}</blockquote>`);
          inBlockquote = false;
          blockquoteContent = [];
        }
        result.push(line);
      }
    }

    // Handle case where blockquote is at the end
    if (inBlockquote) {
      result.push(`<blockquote>${blockquoteContent.join('\n')}</blockquote>`);
    }

    return result.join('\n');
  }

  private processHeadings(html: string): string {
    // H6 to H1 (process in reverse order to handle longer matches first)
    html = html.replace(/^#{6}\s+(.+)$/gm, '<h6>$1</h6>');
    html = html.replace(/^#{5}\s+(.+)$/gm, '<h5>$1</h5>');
    html = html.replace(/^#{4}\s+(.+)$/gm, '<h4>$1</h4>');
    html = html.replace(/^#{3}\s+(.+)$/gm, '<h3>$1</h3>');
    html = html.replace(/^#{2}\s+(.+)$/gm, '<h2>$1</h2>');
    html = html.replace(/^#{1}\s+(.+)$/gm, '<h1>$1</h1>');
    return html;
  }

  private processUnorderedLists(html: string): string {
    const lines = html.split('\n');
    let inList = false;
    let listItems: string[] = [];
    const result: string[] = [];

    for (const line of lines) {
      const match = line.match(/^[\*\-\+]\s+(.+)$/);
      if (match) {
        if (!inList) {
          inList = true;
          listItems = [];
        }
        listItems.push(`<li>${match[1]}</li>`);
      } else {
        if (inList) {
          result.push(`<ul>${listItems.join('')}</ul>`);
          inList = false;
          listItems = [];
        }
        result.push(line);
      }
    }

    // Handle case where list is at the end
    if (inList) {
      result.push(`<ul>${listItems.join('')}</ul>`);
    }

    return result.join('\n');
  }

  private processOrderedLists(html: string): string {
    const lines = html.split('\n');
    let inList = false;
    let listItems: string[] = [];
    const result: string[] = [];

    for (const line of lines) {
      const match = line.match(/^\d+\.\s+(.+)$/);
      if (match) {
        if (!inList) {
          inList = true;
          listItems = [];
        }
        listItems.push(`<li>${match[1]}</li>`);
      } else {
        if (inList) {
          result.push(`<ol>${listItems.join('')}</ol>`);
          inList = false;
          listItems = [];
        }
        result.push(line);
      }
    }

    // Handle case where list is at the end
    if (inList) {
      result.push(`<ol>${listItems.join('')}</ol>`);
    }

    return result.join('\n');
  }

  private processParagraphs(html: string): string {
    // Split by double line breaks
    const blocks = html.split(/\n\n+/);
    
    return blocks.map(block => {
      block = block.trim();
      
      // Don't wrap if already wrapped in a block element
      if (
        block.startsWith('<h') ||
        block.startsWith('<ul>') ||
        block.startsWith('<ol>') ||
        block.startsWith('<pre>') ||
        block.startsWith('<blockquote>')
      ) {
        return block;
      }
      
      // Don't wrap empty blocks
      if (!block) {
        return '';
      }
      
      return `<p>${block}</p>`;
    }).join('');
  }
}
